import { useEffect, useMemo, useState } from 'react'
import { BROKER_LABEL, BROKER_NOTE } from '@shared/brokers'
import { ChevronLeft, ChevronRight, SlidersHorizontal, Workflow } from 'lucide-react'
import type { WorkerDetail, WorkerEventRecord, WorkerRecord } from '@shared/worker'
import { ViewHeader } from '../shell/ViewHeader'
import { useStore } from '../../store'
import { useWorker, useWorkerGroups, useWorkers } from '../../hooks/useWorker'
import { workersApi } from '../../lib/api'
import { cn } from '../../lib/utils'
import { StopAllWorkers } from '../portfolio/IbkrPanel'
import { StatusPill, WorkerCard, usd } from './WorkerCard'
import { WorkerFlow } from './WorkerFlow'
import { OrderCard, TicketPill } from './OrderCard'
import { useOrders } from '../../hooks/useOrder'
import type { TicketRecord } from '@shared/orders'
import type { GroupRecord } from '@shared/groups'
import { GroupPill, WorkerGroupCard } from './WorkerGroupCard'
import { Journal } from './Journal'
import { WatcherCard } from './WatcherCard'
import { useWatchers } from '../../hooks/useWatcher'

const RUNNING = new Set(['waiting', 'working', 'open', 'closing', 'error'])
type Tab = 'running' | 'drafts' | 'history' | 'watchers' | 'groups' | 'orders' | 'journal'
const tabOf = (w: WorkerRecord): Tab => (RUNNING.has(w.status) ? 'running' : w.status === 'draft' ? 'drafts' : 'history')

/** Worst case at the CURRENT stops for what is held or still pending (display estimate). */
function riskNow(w: WorkerRecord): number {
  if (w.status === 'waiting' || !w.runtime) return w.status === 'waiting' ? w.summary.maxLoss : 0
  const long = w.side === 'long'
  return w.runtime.lots.reduce((a, x) => {
    const shares = x.open + (x.entryDone ? 0 : x.qty - x.entryFilled)
    // Bought lots at the average fill; lots still waiting (e.g. dip adds) at their own entry price.
    const planned = w.plan.lots.find((p) => p.lot === x.lot)?.entry?.ref ?? w.plan.entryRef
    const entry = x.entryFilled > 0 ? (w.runtime!.avgEntry ?? planned) : planned
    return a + Math.max(0, long ? entry - x.stop : x.stop - entry) * shares
  }, 0)
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'up' | 'down' }) {
  return (
    <div className="min-w-0 rounded-xl border border-[var(--line-soft)] px-3.5 py-3">
      <div className="text-[10px] uppercase tracking-wide text-fg-faint">{label}</div>
      <div className={cn('num mt-1 truncate text-[20px] font-semibold', tone === 'up' ? 'text-up' : tone === 'down' ? 'text-down' : 'text-fg')}>{value}</div>
      {sub && <div className="mt-0.5 truncate text-[11px] text-fg-subtle">{sub}</div>}
    </div>
  )
}

const when = (t: number) => {
  const d = new Date(t)
  const today = new Date().toDateString() === d.toDateString()
  return today ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function Row({ w, onOpen, group }: { w: WorkerRecord; onOpen: () => void; group?: string }) {
  const open = w.runtime?.lots.reduce((a, x) => a + x.open, 0) ?? 0
  const realized = w.runtime?.realized ?? 0
  return (
    <button
      onClick={onOpen}
      className="flex w-full items-center gap-3 border-t border-[var(--separator)] px-4 py-3 text-left transition-colors first:border-t-0 hover:bg-[var(--color-surface-2)]"
    >
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-[13.5px] font-semibold text-fg">{w.name}</span>
          {group && (
            <span className="shrink-0 rounded-md bg-[var(--color-inset)] px-1.5 py-px text-[10px] text-fg-subtle" title="Member of a worker group">
              {group} · {w.groupKey}
            </span>
          )}
          {w.runtime?.attention && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-down" title={w.runtime.attention} />}
        </span>
        <span className="num mt-0.5 block truncate text-[11.5px] text-fg-subtle">
          {w.summary.headline} · {when(w.createdAt)}
        </span>
      </span>
      {w.runtime && w.status !== 'draft' && (
        <span className="hidden shrink-0 text-right sm:block">
          <span className={cn('num block text-[12.5px] font-medium', realized > 0 ? 'text-up' : realized < 0 ? 'text-down' : 'text-fg-muted')}>
            {usd(realized, true)}
          </span>
          <span className="num block text-[10.5px] text-fg-faint">{open ? `${open} sh open` : 'flat'}</span>
        </span>
      )}
      <StatusPill status={w.status} />
      <ChevronRight size={15} className="shrink-0 text-fg-faint" />
    </button>
  )
}

const EVENT_DOT: Record<string, string> = {
  fill: 'bg-up',
  rule: 'bg-fg',
  action: 'bg-fg-muted',
  modified: 'bg-fg-muted',
  lifecycle: 'bg-fg',
  'broker-error': 'bg-down',
  'modify-failed': 'bg-down',
  attention: 'bg-down',
  fault: 'bg-down',
  reprotected: 'bg-down',
  'close-order': 'bg-down',
  'ai-review': 'bg-fg',
  proposal: 'bg-fg',
  'ask-ai-failed': 'bg-down',
  pause: 'bg-down',
  'band-end': 'bg-down',
  'band-in': 'bg-up',
}

function Timeline({ events }: { events: WorkerEventRecord[] }) {
  const [all, setAll] = useState(false)
  const shown = all ? events : events.slice(0, 40)
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-[var(--line-soft)] px-3.5 py-2.5">
        <span className="text-[13px] font-semibold text-fg">Timeline</span>
        <span className="text-[11px] text-fg-faint">{events.length} events · newest first</span>
      </div>
      <ol className="max-h-[520px] overflow-y-auto px-3.5 py-2">
        {shown.map((e) => (
          <li key={e.id} className="relative flex gap-2.5 py-1.5 text-[12px]">
            <span className="num w-[54px] shrink-0 pt-px text-[10.5px] text-fg-faint" title={new Date(e.at).toLocaleString()}>
              {new Date(e.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
            </span>
            <span className={cn('mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full', EVENT_DOT[e.kind] ?? 'bg-[var(--line)]')} />
            <span className="min-w-0 text-fg-muted">{e.message}</span>
          </li>
        ))}
        {events.length > shown.length && (
          <li className="py-1.5">
            <button onClick={() => setAll(true)} className="text-[11.5px] text-fg-subtle hover:text-fg">
              Show all {events.length}
            </button>
          </li>
        )}
      </ol>
    </div>
  )
}

function Detail({ id, onBack }: { id: string; onBack: () => void }) {
  const { worker: w, error } = useWorker(id)
  if (error && !w) return <div className="p-6 text-[13px] text-fg-subtle">{error}</div>
  if (!w) return <div className="m-6 h-40 animate-pulse rounded-xl bg-[var(--color-surface-2)]" />
  return <DetailBody w={w} onBack={onBack} />
}

function DetailBody({ w, onBack }: { w: WorkerDetail; onBack: () => void }) {
  const lastEvent = w.events[0]?.at
  return (
    <div className="mx-auto w-full max-w-6xl px-3 py-4 sm:px-5">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={onBack} className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-[12.5px] text-fg-subtle hover:bg-[var(--color-surface-2)] hover:text-fg">
          <ChevronLeft size={15} /> Workers
        </button>
        <span className="text-fg-faint">/</span>
        <span className="min-w-0 truncate text-[14px] font-semibold text-fg">{w.name}</span>
        <StatusPill status={w.status} />
      </div>

      <div className="card mt-3 p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-fg-faint">Flow · live {BROKER_LABEL[w.spec.broker ?? 'ibkr']} order states</span>
          <span className="num text-[10.5px] text-fg-faint">
            {w.orders.length} order{w.orders.length === 1 ? '' : 's'} at {BROKER_LABEL[w.spec.broker ?? 'ibkr']}{lastEvent ? ` · last event ${new Date(lastEvent).toLocaleTimeString()}` : ''} · plan #{w.planHash.slice(0, 8)}
          </span>
        </div>
        <WorkerFlow w={w} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
        <WorkerCard id={w.id} variant="page" />
        <div className="min-w-0 space-y-4 self-start lg:sticky lg:top-2">
          <Timeline events={w.events} />
        </div>
      </div>
    </div>
  )
}

/** Chat orders (single IBKR orders from chat), newest first; a row opens its card. */
/** The plan while the order can still fill; what actually happened once it has ended. */
function ticketLine(t: TicketRecord): string {
  if (t.status === 'draft' || t.status === 'working') return t.summary.positionText
  if (!t.orders.length) return 'never sent to the broker'
  const filled = t.fills.filter((f) => f.key === 'O').reduce((a, f) => a + f.shares, 0)
  if (!filled) return 'nothing filled'
  const avg = t.fills.filter((f) => f.key === 'O').reduce((a, f) => a + f.shares * f.price, 0) / filled
  return `${t.spec.action === 'BUY' ? 'bought' : 'sold'} ${filled} @ ${avg.toFixed(2)}`
}

/** Worker groups, newest first; a row opens the group's card (members, links, combined P&L). */
function GroupsList({ groups }: { groups: GroupRecord[] }) {
  const [open, setOpen] = useState<string | null>(null)
  if (!groups.length)
    return (
      <div className="flex flex-col items-center px-4 py-10 text-center">
        <Workflow size={22} className="text-fg-faint" />
        <div className="mt-2 text-[13px] font-medium text-fg-muted">No worker groups yet</div>
        <div className="mt-1 max-w-md text-[12px] text-fg-subtle">
          Link workers in chat: “KO 80’den ya da PEP 120’den al, hangisi önce dolarsa diğerini iptal et”, a pair or hedge, or “toplam zarar 150$ olursa
          hepsini kapat” — then say “worker grubu oluştur”. One card, one approval.
        </div>
      </div>
    )
  return (
    <>
      {groups.map((g) => (
        <div key={g.id} className="border-t border-[var(--separator)] first:border-t-0">
          <button
            onClick={() => setOpen(open === g.id ? null : g.id)}
            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--color-surface-2)]"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold text-fg">{g.name}</span>
              <span className="num mt-0.5 block truncate text-[11.5px] text-fg-subtle">
                {g.summary.headline} · {when(g.createdAt)}
              </span>
            </span>
            {g.runtime?.pnl != null && g.status === 'active' && (
              <span className={cn('num hidden shrink-0 text-[12.5px] font-medium sm:block', g.runtime.pnl > 0 ? 'text-up' : g.runtime.pnl < 0 ? 'text-down' : 'text-fg-muted')}>
                {usd(g.runtime.pnl, true)}
              </span>
            )}
            <GroupPill status={g.status} />
            <ChevronRight size={15} className={cn('shrink-0 text-fg-faint transition-transform', open === g.id && 'rotate-90')} />
          </button>
          {open === g.id && (
            <div className="px-3 pb-3">
              <WorkerGroupCard id={g.id} variant="page" />
            </div>
          )}
        </div>
      ))}
    </>
  )
}

function OrdersList({ tickets }: { tickets: TicketRecord[] }) {
  const [open, setOpen] = useState<string | null>(null)
  if (!tickets.length)
    return (
      <div className="flex flex-col items-center px-4 py-10 text-center">
        <Workflow size={22} className="text-fg-faint" />
        <div className="mt-2 text-[13px] font-medium text-fg-muted">No chat orders yet</div>
        <div className="mt-1 max-w-sm text-[12px] text-fg-subtle">Tell Libera “6 PAYX al 101 limit” or “AAPL’ın yarısını sat” — nothing is sent until you approve its card.</div>
      </div>
    )
  return (
    <>
      {tickets.map((t) => (
        <div key={t.id} className="border-t border-[var(--separator)] first:border-t-0">
          <button
            onClick={() => setOpen(open === t.id ? null : t.id)}
            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--color-surface-2)]"
          >
            <span className="min-w-0 flex-1">
              <span className="num block truncate text-[13px] font-semibold text-fg">{t.summary.headline}</span>
              <span className="num mt-0.5 block truncate text-[11.5px] text-fg-subtle">
                {ticketLine(t)} · {when(t.createdAt)}
              </span>
            </span>
            <TicketPill status={t.status} />
            <ChevronRight size={15} className={cn('shrink-0 text-fg-faint transition-transform', open === t.id && 'rotate-90')} />
          </button>
          {open === t.id && (
            <div className="px-3 pb-3">
              <OrderCard id={t.id} variant="page" />
            </div>
          )}
        </div>
      ))}
    </>
  )
}

export function WorkersPage() {
  const { workers, error } = useWorkers()
  const { tickets } = useOrders()
  const { groups } = useWorkerGroups()
  const groupName = useMemo(() => new Map((groups ?? []).map((g) => [g.id, g.name])), [groups])
  const focus = useStore((s) => s.workerFocus)
  const openWorker = useStore((s) => s.openWorker)
  const [tab, setTab] = useState<Tab>('running')
  const { watchers } = useWatchers()
  const focusSettings = useStore((s) => s.focusSettings)
  const [limitsOn, setLimitsOn] = useState<boolean | null>(null)
  useEffect(() => {
    workersApi.limits().then((l) => setLimitsOn(l.enabled), () => setLimitsOn(null))
  }, [])

  const counts = useMemo(() => {
    const c = { running: 0, drafts: 0, history: 0, watchers: (watchers ?? []).filter((w) => w.status !== 'done').length, groups: groups?.length ?? 0, orders: tickets?.length ?? 0, journal: 0 }
    for (const w of workers ?? []) {
      c[tabOf(w)]++
      if (!w.revises && w.runtime?.entryFilledAt != null) c.journal++
    }
    return c
  }, [workers, tickets, groups, watchers])
  const stats = useMemo(() => {
    const running = (workers ?? []).filter((w) => RUNNING.has(w.status))
    return {
      running: running.length,
      shares: running.reduce((a, w) => a + (w.runtime?.lots.reduce((b, x) => b + x.open, 0) ?? 0), 0),
      risk: running.reduce((a, w) => a + riskNow(w), 0),
      realized: (workers ?? []).reduce((a, w) => a + (w.runtime?.realized ?? 0), 0),
      attention: running.filter((w) => w.status === 'error' || w.runtime?.attention).length,
    }
  }, [workers])

  // Land on the tab that holds a freshly drafted worker.
  useEffect(() => {
    if (!workers || focus) return
    if (!counts.running && counts.drafts) setTab('drafts')
  }, [workers, focus, counts.running, counts.drafts])

  const list = (workers ?? []).filter((w) => tabOf(w) === tab)

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-[var(--color-base)]">
      <ViewHeader title="Workers" />
      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        {focus ? (
          <Detail id={focus} onBack={() => openWorker(null)} />
        ) : (
          <div className="mx-auto w-full max-w-6xl px-3 py-5 sm:px-5 sm:py-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h1 className="text-[22px] font-semibold text-fg">Workers</h1>
                <p className="mt-0.5 text-[12.5px] text-fg-subtle">
                  Trade plans executed at Interactive Brokers — every stop lives at the broker. Create one in chat by saying “worker oluştur”.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => focusSettings('trading')}
                  title="Global risk limits live in Settings"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] px-2.5 py-1 text-[11.5px] text-fg-muted hover:text-fg"
                >
                  <SlidersHorizontal size={13} /> Risk limits: {limitsOn == null ? '…' : limitsOn ? 'on' : 'off'}
                </button>
                <StopAllWorkers />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
              <Stat label="Running" value={String(stats.running)} sub="armed, working or in position" />
              <Stat label="Shares held" value={String(stats.shares)} sub="by running workers" />
              <Stat label="Open risk" value={usd(-stats.risk)} sub="at current stops" tone={stats.risk > 0 ? 'down' : undefined} />
              <Stat label="Realized" value={usd(stats.realized, true)} sub="all workers" tone={stats.realized > 0 ? 'up' : stats.realized < 0 ? 'down' : undefined} />
              <Stat label="Needs attention" value={String(stats.attention)} sub={stats.attention ? 'automation paused on these' : 'all clear'} tone={stats.attention ? 'down' : undefined} />
            </div>

            <div className="mt-5 flex gap-4 border-b border-[var(--line-soft)]">
              {(
                [
                  ['running', 'Running'],
                  ['drafts', 'Awaiting approval'],
                  ['history', 'History'],
                  ['watchers', 'Watchers & scans'],
                  ['groups', 'Groups'],
                  ['orders', 'Orders'],
                  ['journal', 'Journal'],
                ] as const
              ).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  className={cn(
                    '-mb-px border-b-2 px-0.5 pb-2 text-[12.5px] font-medium transition-colors',
                    tab === k ? 'border-[var(--color-accent)] text-fg' : 'border-transparent text-fg-subtle hover:text-fg',
                  )}
                >
                  {label} <span className="num text-fg-faint">{counts[k]}</span>
                </button>
              ))}
            </div>

            {tab === 'watchers' ? (
              <WatchersList watchers={watchers} />
            ) : (
            <div className="card mt-3 overflow-hidden">
              {tab === 'journal' ? (
                <Journal />
              ) : tab === 'orders' ? (
                tickets ? <OrdersList tickets={tickets} /> : <div className="h-32 animate-pulse" />
              ) : tab === 'groups' ? (
                groups ? <GroupsList groups={groups} /> : <div className="h-32 animate-pulse" />
              ) : error && !workers ? (
                <div className="px-4 py-8 text-center text-[12.5px] text-fg-subtle">{error}</div>
              ) : !workers ? (
                <div className="h-32 animate-pulse" />
              ) : list.length === 0 ? (
                <div className="flex flex-col items-center px-4 py-10 text-center">
                  <Workflow size={22} className="text-fg-faint" />
                  <div className="mt-2 text-[13px] font-medium text-fg-muted">
                    {tab === 'running' ? 'No running workers' : tab === 'drafts' ? 'Nothing awaiting approval' : 'No finished workers yet'}
                  </div>
                  <div className="mt-1 max-w-sm text-[12px] text-fg-subtle">
                    Plan a trade with Libera in chat — tiers, ratchets, time exits, triggers on any stock or index — then say “worker oluştur”. Nothing runs
                    until you approve its card.
                  </div>
                </div>
              ) : (
                list.map((w) => <Row key={w.id} w={w} onOpen={() => openWorker(w.id)} group={w.groupId ? groupName.get(w.groupId) ?? 'group' : undefined} />)
              )}
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** Watchers and scans: workers that never trade — active first, the ended ones folded under. */
function WatchersList({ watchers }: { watchers: ReturnType<typeof useWatchers>['watchers'] }) {
  const [showEnded, setShowEnded] = useState(false)
  if (!watchers) return <div className="card mt-3 h-32 animate-pulse" />
  const live = watchers.filter((w) => w.status !== 'done')
  const ended = watchers.filter((w) => w.status === 'done')
  return (
    <div className="mt-3 space-y-3">
      {live.length === 0 && (
        <div className="card flex flex-col items-center px-4 py-10 text-center">
          <div className="text-[13px] font-medium text-fg-muted">No watchers or scans</div>
          <div className="mt-1 max-w-md text-[12px] text-fg-subtle">
            Ask Libera in chat: “PAYX 100.30–101.50 bölgesine girerse haber ver ve sen de bir bak”, “%5’ten fazla düşen S&P hisselerini her 15 dakikada tara”.
            They watch real-time data, place no orders, and tell you — or wake the AI — when something happens.
          </div>
        </div>
      )}
      <div className="grid gap-3 lg:grid-cols-2">
        {live.map((w) => (
          <WatcherCard key={w.id} id={w.id} />
        ))}
      </div>
      {ended.length > 0 && (
        <div>
          <button onClick={() => setShowEnded(!showEnded)} className="text-[12px] text-fg-subtle hover:text-fg">
            {showEnded ? '−' : '+'} {ended.length} ended
          </button>
          {showEnded && (
            <div className="mt-2 grid gap-3 lg:grid-cols-2">
              {ended.map((w) => (
                <WatcherCard key={w.id} id={w.id} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
